import { useRef, useState } from 'react';
import { assistQuestion } from '../services/questions';
import { ApiError } from '@admin/services/api';
import type { AliasPair, QuestionFormValues } from './QuestionForm';
import { questionLanguageVisibility } from './contentLanguages';
import type {
  QuestionAssistPatch,
  QuestionAssistTurn,
  QuestionContentFlag,
} from '@games/word-clash/shared/types';

/**
 * "Ask Claude" chat panel for the question editor. Sends the current form
 * draft + the admin's instruction to POST /admin/questions/assist (backed by
 * the operator's Claude subscription), renders the reply, and — when the model
 * proposes changes — shows a field-by-field DIFF (what's added/removed/changed)
 * with a one-click "Apply to form" button. Nothing here persists: applying only
 * fills the form; the admin still saves through the normal Save button. A
 * "Stop" button cancels an in-flight request.
 */

interface AssistMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Present only on assistant turns that proposed a change. */
  suggestion?: QuestionAssistPatch;
  /** The draft as it was when this suggestion was generated — diff baseline. */
  baseline?: QuestionAssistPatch;
  /** Flipped once the admin applies the suggestion, to disable the button. */
  applied?: boolean;
}

interface AssistPanelProps {
  values: QuestionFormValues;
  applyPatch: (patch: QuestionAssistPatch) => void;
}

function aliasPairsToMap(pairs: AliasPair[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of pairs) {
    const alias = p.alias.trim().toLowerCase();
    const canonical = p.canonical.trim();
    if (alias && canonical) out[alias] = canonical;
  }
  return out;
}

/** Build the patch-shaped draft from the live form values. */
function valuesToDraft(values: QuestionFormValues): QuestionAssistPatch {
  return {
    promptEn: values.promptEn,
    promptTr: values.promptTr,
    category: values.category,
    difficulty: values.difficulty,
    validAnswers: values.validAnswers,
    validAnswersTr: values.validAnswersTr,
    specialAnswers: values.specialAnswers,
    specialAnswersTr: values.specialAnswersTr,
    plausibleWrongAnswers: values.plausibleWrongAnswers,
    plausibleWrongAnswersTr: values.plausibleWrongAnswersTr,
    answerAliases: aliasPairsToMap(values.aliases),
    answerAliasesTr: aliasPairsToMap(values.aliasesTr),
    contentFlags: [...values.contentFlags],
  };
}

// ---------------------------------------------------------------------------
// Diff: what would "Apply" change, relative to the draft at suggestion time?
// ---------------------------------------------------------------------------

type FieldDiff =
  | { kind: 'scalar'; label: string; before: string; after: string }
  | { kind: 'list'; label: string; added: string[]; removed: string[] };

const SCALAR_FIELDS: { key: 'promptEn' | 'promptTr' | 'category' | 'difficulty'; label: string }[] = [
  { key: 'promptEn', label: 'Prompt (EN)' },
  { key: 'promptTr', label: 'Prompt (TR)' },
  { key: 'category', label: 'Category' },
  { key: 'difficulty', label: 'Difficulty' },
];

const LIST_FIELDS: { key: keyof QuestionAssistPatch; label: string }[] = [
  { key: 'validAnswers', label: 'Valid answers (EN)' },
  { key: 'validAnswersTr', label: 'Valid answers (TR)' },
  { key: 'specialAnswers', label: 'Special answers (EN)' },
  { key: 'specialAnswersTr', label: 'Special answers (TR)' },
  { key: 'plausibleWrongAnswers', label: 'Plausible wrong (EN)' },
  { key: 'plausibleWrongAnswersTr', label: 'Plausible wrong (TR)' },
  { key: 'contentFlags', label: 'Content flags' },
];

const ALIAS_FIELDS: { key: 'answerAliases' | 'answerAliasesTr'; label: string }[] = [
  { key: 'answerAliases', label: 'Aliases (EN)' },
  { key: 'answerAliasesTr', label: 'Aliases (TR)' },
];

function aliasMapToStrings(m: Record<string, string> | undefined): string[] {
  return Object.entries(m ?? {}).map(([k, v]) => `${k} → ${v}`);
}

function listDiff(before: string[], after: string[]): { added: string[]; removed: string[] } {
  const beforeSet = new Set(before.map((s) => s.toLowerCase()));
  const afterSet = new Set(after.map((s) => s.toLowerCase()));
  return {
    added: after.filter((s) => !beforeSet.has(s.toLowerCase())),
    removed: before.filter((s) => !afterSet.has(s.toLowerCase())),
  };
}

/** True for a field belonging to a language the question is not served in. */
function isHiddenLanguageField(
  key: string,
  visibility: { showEnglish: boolean; showTurkish: boolean },
): boolean {
  // Non-language keys are never gated. `contentFlags` is what SETS the
  // visibility — hiding it would conceal a change `applyPatch` still makes,
  // e.g. flipping a tr-only question bilingual with nothing on screen to say
  // so. Prompts are server-required in both languages and stay editable, so a
  // proposed prompt change is always actionable.
  if (key === 'contentFlags' || key === 'promptEn' || key === 'promptTr')
    return false;
  const isTr = key.endsWith('Tr');
  return isTr ? !visibility.showTurkish : !visibility.showEnglish;
}

/**
 * Compute the visible diff between the baseline draft and a proposed patch.
 *
 * Rows for a language the question isn't served in are dropped: `applyPatch`
 * refuses to write them, so showing them would advertise a change that
 * silently does nothing when the admin clicks Apply.
 */
function computeDiffs(
  baseline: QuestionAssistPatch,
  patch: QuestionAssistPatch,
  currentFlags: readonly QuestionContentFlag[],
): FieldDiff[] {
  // Derived from the flags the patch PRODUCES, mirroring `applyPatch`. Gating
  // on the CURRENT flags would hide rows that Apply then writes: an assistant
  // reply that turns an en-only question bilingual AND supplies the Turkish
  // content would show only the flag change, and the answers would land
  // unseen. The two gates must key off the same thing or the panel lies about
  // what Apply does — the same defect class as gating `contentFlags` itself.
  const visibility = questionLanguageVisibility(
    patch.contentFlags ?? currentFlags,
  );
  const diffs: FieldDiff[] = [];

  for (const { key, label } of SCALAR_FIELDS) {
    const after = patch[key];
    if (after === undefined) continue;
    const before = baseline[key] ?? '';
    if (String(before) === String(after)) continue;
    diffs.push({ kind: 'scalar', label, before: String(before), after: String(after) });
  }

  for (const { key, label } of LIST_FIELDS) {
    if (isHiddenLanguageField(key, visibility)) continue;
    const after = patch[key] as string[] | undefined;
    if (after === undefined) continue;
    const before = (baseline[key] as string[] | undefined) ?? [];
    const { added, removed } = listDiff(before, after);
    if (added.length === 0 && removed.length === 0) continue;
    diffs.push({ kind: 'list', label, added, removed });
  }

  for (const { key, label } of ALIAS_FIELDS) {
    if (isHiddenLanguageField(key, visibility)) continue;
    const after = patch[key];
    if (after === undefined) continue;
    const { added, removed } = listDiff(
      aliasMapToStrings(baseline[key]),
      aliasMapToStrings(after),
    );
    if (added.length === 0 && removed.length === 0) continue;
    diffs.push({ kind: 'list', label, added, removed });
  }

  return diffs;
}

function SuggestionDiff({ diffs }: { diffs: FieldDiff[] }) {
  if (diffs.length === 0) {
    return (
      <p className="text-xs text-slate-500" data-testid="question-assistant-diff-empty">
        No changes proposed.
      </p>
    );
  }
  return (
    <ul className="space-y-1.5" data-testid="question-assistant-diff">
      {diffs.map((d, i) => (
        <li key={i} className="text-xs">
          <span className="font-medium text-slate-700">{d.label}:</span>{' '}
          {d.kind === 'scalar' ? (
            <span>
              <span className="text-red-600 line-through">{d.before || '∅'}</span>
              {' → '}
              <span className="text-emerald-700">{d.after || '∅'}</span>
            </span>
          ) : (
            <span className="inline-flex flex-wrap gap-1 align-top">
              {d.added.map((a, j) => (
                <span
                  key={`a-${j}`}
                  className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-800"
                >
                  +{a}
                </span>
              ))}
              {d.removed.map((r, j) => (
                <span
                  key={`r-${j}`}
                  className="rounded bg-red-100 px-1.5 py-0.5 text-red-700 line-through"
                >
                  {r}
                </span>
              ))}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function AssistPanel({ values, applyPatch }: AssistPanelProps) {
  const [messages, setMessages] = useState<AssistMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  async function handleSend() {
    const instruction = input.trim();
    if (!instruction || sending) return;
    setError(null);
    setSending(true);

    // History = prior turns (assistant turns carry the reply text only).
    const history: QuestionAssistTurn[] = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));
    const baseline = valuesToDraft(values);

    setMessages((prev) => [...prev, { role: 'user', content: instruction }]);
    setInput('');

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await assistQuestion(
        { instruction, draft: baseline, history },
        controller.signal,
      );
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.reply,
          suggestion: res.suggestion,
          baseline,
        },
      ]);
    } catch (err) {
      const aborted =
        controller.signal.aborted ||
        (err instanceof DOMException && err.name === 'AbortError');
      if (aborted) {
        // User cancelled — quietly roll back the optimistic turn, no error.
        setMessages((prev) => prev.slice(0, -1));
        setInput(instruction);
      } else if (
        err instanceof ApiError &&
        err.error === 'question_assist_unavailable'
      ) {
        setUnavailable(true);
        setMessages((prev) => prev.slice(0, -1));
        setInput(instruction);
      } else {
        const message =
          err instanceof ApiError
            ? `${err.error}: ${err.message}`
            : err instanceof Error
              ? err.message
              : 'Assistant request failed';
        setError(message);
        setMessages((prev) => prev.slice(0, -1));
        setInput(instruction);
      }
    } finally {
      abortRef.current = null;
      setSending(false);
    }
  }

  function handleStop() {
    abortRef.current?.abort();
  }

  function handleApply(index: number) {
    const msg = messages[index];
    if (!msg?.suggestion) return;
    applyPatch(msg.suggestion);
    setMessages((prev) =>
      prev.map((m, i) => (i === index ? { ...m, applied: true } : m)),
    );
  }

  if (unavailable) {
    return (
      <div
        data-testid="question-assistant-unavailable"
        className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
      >
        <p className="font-semibold">AI assistant unavailable</p>
        <p className="mt-1">
          This server has no Claude credential configured
          (<code>CLAUDE_CODE_OAUTH_TOKEN</code>). Ask an operator to set it to
          enable in-app authoring help.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="question-assistant"
      className="flex flex-col rounded-lg border border-slate-200 bg-white max-h-[40rem]"
    >
      <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
        <span className="text-sm font-semibold text-slate-900">Ask Claude</span>
        <span className="text-xs text-slate-500">authoring help</span>
      </div>

      <div
        className="flex-1 overflow-y-auto p-3 space-y-3 min-h-[10rem]"
        data-testid="question-assistant-messages"
      >
        {messages.length === 0 && (
          <p className="text-xs text-slate-500">
            Ask for help with this question — e.g. “add 10 more valid answers”,
            “make the Turkish version more natural”, “suggest plausible wrong
            answers”, or “tighten the prompt”. You’ll see exactly what changes
            before applying; nothing saves until you click Save.
          </p>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            data-testid={`question-assistant-message-${m.role}`}
            className={
              m.role === 'user'
                ? 'ml-6 rounded-lg bg-slate-800 px-3 py-2 text-sm text-white'
                : 'mr-2 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-800'
            }
          >
            <p className="whitespace-pre-wrap">{m.content}</p>
            {m.role === 'assistant' && m.suggestion && m.baseline && (
              <div className="mt-2 border-t border-slate-200 pt-2 space-y-2">
                <SuggestionDiff
                  diffs={computeDiffs(
                    m.baseline,
                    m.suggestion,
                    values.contentFlags,
                  )}
                />
                <button
                  type="button"
                  onClick={() => handleApply(i)}
                  disabled={m.applied}
                  data-testid={`question-assistant-apply-${i}`}
                  className="text-xs rounded bg-emerald-600 text-white px-2 py-1 hover:bg-emerald-500 disabled:opacity-50"
                >
                  {m.applied ? 'Applied ✓ (review the highlighted fields)' : 'Apply to form'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {error && (
        <div
          role="alert"
          data-testid="question-assistant-error"
          className="mx-3 mb-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700"
        >
          {error}
        </div>
      )}

      <div className="border-t border-slate-200 p-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter inserts a newline.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void handleSend();
            }
          }}
          disabled={sending}
          rows={2}
          placeholder="Ask Claude to improve this question…"
          data-testid="question-assistant-input"
          className="block w-full resize-none rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:bg-slate-100"
        />
        <div className="mt-2 flex justify-end gap-2">
          {sending && (
            <button
              type="button"
              onClick={handleStop}
              data-testid="question-assistant-stop"
              className="text-sm rounded border border-red-300 text-red-700 px-3 py-1.5 hover:bg-red-50"
            >
              Stop
            </button>
          )}
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={sending || !input.trim()}
            data-testid="question-assistant-send"
            className="text-sm rounded bg-slate-800 text-white px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50"
          >
            {sending ? 'Thinking…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
